import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link, Navigate, useNavigate } from 'react-router-dom';
import LandingPage from './components/LandingPage.jsx';
import Registration from './components/Registration.jsx';
import Login from './components/Login.jsx';
import PhotoUpload from './components/PhotoUpload.jsx';
import Dashboard from './components/Dashboard.jsx';
import AdminPanel from './components/AdminPanel.jsx';
import AdminLogin from './components/AdminLogin.jsx';
import CaptureShell from './capture/CaptureShell.jsx';
import './App.css';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  const isUserPage = location.pathname.startsWith('/upload/') || location.pathname.startsWith('/dashboard/');
  const isAdminPage = location.pathname === '/admin';
  const userName = sessionStorage.getItem('userName');
  const adminToken = sessionStorage.getItem('adminToken');

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  const handleUserLogout = () => {
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('userName');
    setMenuOpen(false);
    navigate('/');
  };

  const handleAdminLogout = () => {
    sessionStorage.removeItem('adminToken');
    setMenuOpen(false);
    navigate('/admin/login');
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
  };

  const userInitial = userName ? userName.trim().charAt(0).toUpperCase() : 'U';

  return (
    <header className="App-header">
      <div className="header-left">
        <button onClick={handleLogoClick} className="header-logo" title="Imaginary Home">
          <img src="/logo192.png" alt="Imaginary Logo" className="brand-logo-img" />
          <div className="brand-text-col">
            <span className="brand-title">Imaginary</span>
          </div>
        </button>
      </div>

      <div className="header-right">
        {/* Admin Quick Access Button */}
        <Link
          to={adminToken ? "/admin" : "/admin/login"}
          className={`header-admin-nav-btn ${isAdminPage ? 'active' : ''}`}
          title="Open Admin Control Panel"
        >
          <span className="admin-nav-icon">🛡️</span>
          <span className="admin-nav-text">Admin</span>
        </Link>

        {/* Dedicated Profile Button in Navbar - Visible when user is in the Dashboard */}
        {location.pathname.startsWith('/dashboard/') && (
          <button
            type="button"
            className="header-profile-nav-btn"
            onClick={() => {
              window.dispatchEvent(new CustomEvent('open-user-profile-modal'));
            }}
            title="View / Edit Profile Information"
          >
            <span className="profile-nav-icon">👤</span>
            <span className="profile-nav-text">My Profile</span>
          </button>
        )}

        {/* Notification Bell (for active user sessions) */}
        {userName && (
          <div className="header-notif-wrapper" ref={notifRef}>
            <button
              type="button"
              className="header-bell-btn"
              onClick={() => setNotifOpen(!notifOpen)}
              aria-label="Notifications"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="bell-svg">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <span className="header-bell-badge">2</span>
            </button>

            {notifOpen && (
              <div className="notif-dropdown-card">
                <div className="notif-header">
                  <strong>Notifications</strong>
                  <span className="notif-tag">2 New</span>
                </div>
                <div className="notif-divider" />
                <div className="notif-list">
                  <div className="notif-card-item unread">
                    <strong>Task Active: Age Progression</strong>
                    <p>Complete your 3 historical photos, 5 selfies, and 8 poses.</p>
                    <span className="notif-card-time">Today, 2:30 PM</span>
                  </div>
                  <div className="notif-card-item unread">
                    <strong>Variation B Angles Updated</strong>
                    <p>Diagonal tolerance bands set to ±20°–35° pitch & yaw.</p>
                    <span className="notif-card-time">Yesterday</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Avatar Menu */}
        <div ref={menuRef}>
        {isUserPage || isAdminPage || userName ? (
          <div className="avatar-menu-wrapper">
            <button
              type="button"
              className="avatar-profile-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="User Profile Menu"
              aria-expanded={menuOpen}
            >
              <div className="avatar-circle">
                {isAdminPage ? '🛡️' : userInitial}
              </div>
              <span className="avatar-name-label">
                {isAdminPage ? 'Admin' : (userName || 'Account')}
              </span>
              <span className="avatar-chevron">{menuOpen ? '▴' : '▾'}</span>
            </button>

            {menuOpen && (
              <div className="profile-dropdown-card">
                <div className="dropdown-user-header">
                  <div className="dropdown-user-initial">
                    {isAdminPage ? '🛡️' : userInitial}
                  </div>
                  <div className="dropdown-user-info">
                    <strong>{isAdminPage ? 'Administrator' : (userName || 'Verified User')}</strong>
                    <span className="dropdown-user-tag">{isAdminPage ? 'System Admin' : 'Participant'}</span>
                  </div>
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-links">
                  <Link
                    to="/"
                    className={`dropdown-link-item ${location.pathname === '/' ? 'active' : ''}`}
                  >
                    🏠 Home Portal
                  </Link>

                  {userName && (
                    <>
                      <Link
                        to={`/upload/${encodeURIComponent(userName)}`}
                        className={`dropdown-link-item ${location.pathname.startsWith('/upload/') ? 'active' : ''}`}
                      >
                        📸 Verification Capture
                      </Link>
                      <Link
                        to={`/dashboard/${encodeURIComponent(userName)}`}
                        className={`dropdown-link-item ${location.pathname.startsWith('/dashboard/') ? 'active' : ''}`}
                      >
                        📊 Progress Dashboard
                      </Link>
                    </>
                  )}

                  {/* Admin link discreetly inside menu */}
                  {(adminToken || isAdminPage) ? (
                    <Link
                      to="/admin"
                      className={`dropdown-link-item ${isAdminPage ? 'active' : ''}`}
                    >
                      🛡️ Admin Control Panel
                    </Link>
                  ) : (
                    <Link
                      to="/admin/login"
                      className="dropdown-link-item text-muted"
                    >
                      🔒 Staff / Admin Sign In
                    </Link>
                  )}
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-footer">
                  {isAdminPage ? (
                    <button onClick={handleAdminLogout} className="dropdown-logout-btn">
                      🚪 Logout Admin
                    </button>
                  ) : (
                    <button onClick={handleUserLogout} className="dropdown-logout-btn">
                      🚪 Log Out
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="header-auth-pills">
            {location.pathname !== '/login' && (
              <Link to="/login" className="header-auth-link login-pill">
                Sign In
              </Link>
            )}
            {location.pathname !== '/register' && (
              <Link to="/register" className="header-auth-link register-pill">
                Register
              </Link>
            )}
          </div>
        )}
        </div>
      </div>
    </header>
  );
};

const AppContent = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isLanding = location.pathname === '/';

  return (
    <div className="App">
      {!isLanding && <Header />}
      <main className={isAdmin ? 'admin-main-wrapper' : isLanding ? 'landing-main-wrapper' : 'user-main-wrapper'}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/register" element={<Registration />} />
          <Route path="/login" element={<Login />} />
          <Route path="/upload/:userName" element={<PhotoUpload />} />
          <Route path="/dashboard/:userName" element={<Dashboard />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/capture" element={<CaptureShell />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
